import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { CancelledActivityVoucherRow, incomeExpenseApi } from '../../../api/incomeExpenseApi';

const formatCurrency = (n: number) =>
  `${new Intl.NumberFormat('vi-VN').format(Math.round(n))}đ`;

interface CancelledActivityVouchersModalProps {
  kind: 'revenue' | 'expenditure';
  buildingId: string;
  onClose: () => void;
}

export const CancelledActivityVouchersModal: React.FC<CancelledActivityVouchersModalProps> = ({
  kind,
  buildingId,
  onClose,
}) => {
  const [rows, setRows] = useState<CancelledActivityVoucherRow[]>([]);
  const [loading, setLoading] = useState(false);
  const isRevenue = kind === 'revenue';
  const title = isRevenue ? 'Danh sách phiếu thu đã huỷ' : 'Danh sách phiếu chi đã huỷ';

  const load = useCallback(async () => {
    if (!buildingId) return;
    setLoading(true);
    try {
      const data = isRevenue
        ? await incomeExpenseApi.listCancelledRevenueVouchers(buildingId)
        : await incomeExpenseApi.listCancelledExpenditureVouchers(buildingId);
      setRows(data || []);
    } catch {
      toast.error('Không thể tải danh sách phiếu đã huỷ');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [buildingId, isRevenue]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col">
        <div className="border-b px-6 py-4 flex items-center justify-between shrink-0">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-700 text-xl leading-none">
            ×
          </button>
        </div>
        <div className="overflow-auto flex-1 p-4">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left">Số bút toán</th>
                <th className="px-3 py-2 text-left">Tên phiếu</th>
                <th className="px-3 py-2 text-left">Loại</th>
                <th className="px-3 py-2 text-right">Số tiền</th>
                <th className="px-3 py-2 text-left">Ngày thực thu/chi</th>
                <th className="px-3 py-2 text-left">Ngày huỷ</th>
                <th className="px-3 py-2 text-left">Người huỷ</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-gray-500">Đang tải...</td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-gray-500">Chưa có phiếu đã huỷ</td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.id} className="border-t">
                    <td className="px-3 py-2 whitespace-nowrap">{row.journalNumber || '—'}</td>
                    <td className="px-3 py-2">{row.name}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {row.revenueCategory?.name || row.expenditureCategory?.name || '—'}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">{formatCurrency(row.totalAmount)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {new Date(row.orderDate).toLocaleDateString('vi-VN')}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {row.cancelledAt ? new Date(row.cancelledAt).toLocaleString('vi-VN') : '—'}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">{row.cancelledBy?.name || '—'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CancelledActivityVouchersModal;
